import { useState } from "react";

const dimensions = [
    {
        name: "Values",
        score: 98,
        orbit: 0.9,
        detail: "What you protect, prioritize, forgive, and refuse to compromise on.",
        clue: "Shared non-negotiables make the first month feel calmer.",
    },
    {
        name: "Lifestyle",
        score: 91,
        orbit: 0.72,
        detail: "Daily rhythm, social appetite, money habits, work shape, and weekend defaults.",
        clue: "Compatibility rises when ordinary Tuesdays line up.",
    },
    {
        name: "Humor",
        score: 94,
        orbit: 0.55,
        detail: "How you tease, defuse tension, send memes, and recover from awkwardness.",
        clue: "A shared laugh is treated as emotional infrastructure.",
    },
    {
        name: "Ambition",
        score: 88,
        orbit: 0.82,
        detail: "Career appetite, creative drive, risk tolerance, and the life you are building toward.",
        clue: "We look for direction that complements instead of competes.",
    },
    {
        name: "Attachment",
        score: 93,
        orbit: 0.65,
        detail: "How you seek reassurance, handle distance, repair conflict, and feel safe.",
        clue: "This helps match people who can regulate with each other.",
    },
    {
        name: "Love language",
        score: 90,
        orbit: 0.48,
        detail: "The way affection lands: words, time, touch, gestures, help, and consistency.",
        clue: "Different languages can work when both people understand the translation.",
    },
    {
        name: "Politics",
        score: 86,
        orbit: 0.9,
        detail: "Civic values, tolerance boundaries, public life, and the topics that need alignment.",
        clue: "We separate healthy difference from future deal-breakers.",
    },
    {
        name: "Spirituality",
        score: 84,
        orbit: 0.72,
        detail: "Meaning-making, ritual, faith, skepticism, awe, and how you explain the world.",
        clue: "Belief systems matter most when life gets difficult.",
    },
    {
        name: "Conflict style",
        score: 96,
        orbit: 0.55,
        detail: "How you argue, pause, apologize, revisit hard topics, and repair trust.",
        clue: "Green flag: two people who can disagree without disappearing.",
    },
    {
        name: "Pace of life",
        score: 89,
        orbit: 0.82,
        detail: "Fast or slow, planned or spontaneous, quiet or packed, rooted or restless.",
        clue: "The right pace makes commitment feel breathable.",
    },
    {
        name: "Family vision",
        score: 87,
        orbit: 0.65,
        detail: "Children, chosen family, caregiving, location, holidays, and long-term home shape.",
        clue: "We surface future-shaping questions before they become surprises.",
    },
    {
        name: "Intimacy",
        score: 92,
        orbit: 0.48,
        detail: "Emotional closeness, physical comfort, vulnerability, privacy, and trust pace.",
        clue: "Good matches respect both chemistry and timing.",
    },
];

export default function Compatibility() {
    const [activeIndex, setActiveIndex] = useState(0);
    const active = dimensions[activeIndex];

    return (
        <section
            id="compatibility"
            className="relative py-28 lg:py-36 bg-[var(--ink)] text-[var(--paper)] overflow-hidden border-y-2 border-[var(--ink)]"
            data-testid="compatibility-section"
        >
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, var(--paper) 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
            </div>

            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.08fr_0.92fr] gap-14 items-center">
                <div className="order-2 lg:order-1 reveal relative flex items-center justify-center">
                    <div
                        className="compatibility-system relative w-[min(88vw,420px)] h-[min(88vw,420px)] lg:w-[560px] lg:h-[560px]"
                        data-testid="compatibility-orbit-system"
                        onMouseLeave={() => setActiveIndex(0)}
                    >
                        {/* Concentric dashed rings */}
                        {[1, 0.78, 0.56, 0.34].map((scale, i) => (
                            <div
                                key={i}
                                className="compatibility-ring absolute inset-0 rounded-full border-2 border-dashed border-[var(--paper)] opacity-30"
                                style={{
                                    transform: `scale(${scale})`,
                                    animationDelay: `${i * -3}s`,
                                    animationDuration: `${32 + i * 8}s`,
                                }}
                            />
                        ))}
                        {/* Signals */}
                        {dimensions.map((d, i) => {
                            const angle = (i / dimensions.length) * Math.PI * 2;
                            const x = 50 + Math.cos(angle - Math.PI / 2) * d.orbit * 50;
                            const y = 50 + Math.sin(angle - Math.PI / 2) * d.orbit * 50;
                            const isActive = activeIndex === i;
                            return (
                                <button
                                    key={d.name}
                                    type="button"
                                    onMouseEnter={() => setActiveIndex(i)}
                                    onFocus={() => setActiveIndex(i)}
                                    onBlur={() => setActiveIndex(0)}
                                    onClick={() => setActiveIndex(i)}
                                    className={`compatibility-planet absolute flex w-32 -translate-x-1/2 -translate-y-1/2 flex-col items-center text-center outline-none transition-transform duration-300 ease-out ${
                                        isActive ? "z-30 scale-105" : "z-10 hover:scale-[1.03]"
                                    }`}
                                    style={{
                                        left: `${x}%`,
                                        top: `${y}%`,
                                        animationDelay: `${i * -0.65}s`,
                                    }}
                                    data-testid={`dimension-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    aria-label={`Show ${d.name} compatibility detail`}
                                >
                                    <span
                                        className={`rounded-full border-2 transition-colors duration-300 ease-out ${
                                            isActive
                                                ? "h-4 w-4 bg-[var(--paper)] border-[var(--hot)] shadow-[0_0_16px_rgba(255,45,107,0.7)]"
                                                : "h-4 w-4 bg-[var(--hot)] border-[var(--paper)]/50 shadow-[0_0_10px_rgba(255,45,107,0.45)]"
                                        }`}
                                    />
                                    <span
                                        className={`hidden lg:block mt-2 font-mono text-[9px] uppercase tracking-[0.16em] whitespace-nowrap transition-colors duration-300 ${
                                            isActive
                                                ? "text-[var(--hot)]"
                                                : "text-[var(--paper)]/80"
                                        }`}
                                    >
                                        {d.name}
                                    </span>
                                </button>
                            );
                        })}
                        {/* Center */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-44 h-44 rounded-full border-2 border-[var(--hot)] flex items-center justify-center bg-[var(--ink)]">
                                <div className="text-center">
                                    <div className="font-display text-5xl text-[var(--hot)] leading-none">
                                        96%
                                    </div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] mt-2 opacity-70">
                                        aligned
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="order-1 lg:order-2 reveal">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-5 text-[var(--hot)]">
                        Sect. D — The Science
                    </div>
                    <h2 className="font-display text-6xl lg:text-9xl leading-[0.85] tracking-tighter">
                        12 sides of{" "}
                        <span className="font-italic-serif text-[var(--hot)]">
                            you.
                        </span>
                    </h2>
                    <p className="mt-7 max-w-md leading-relaxed text-[var(--paper)] opacity-80">
                        We don't guess your "type" from selfies. Our model
                        weighs your values, attachment style, humor, life pace
                        and nine more nuanced signals. Five matches, all of
                        them surprisingly aligned.
                    </p>
                    <div
                        className="mt-9 min-h-[310px] max-w-lg border-2 border-[var(--paper)]/30 bg-[var(--paper)]/8 p-7"
                        data-testid="compatibility-detail-panel"
                    >
                        <div className="flex items-start justify-between gap-5">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--hot)]">
                                    Active signal
                                </div>
                                <h3 className="mt-3 font-display text-5xl leading-none">
                                    {active.name}
                                </h3>
                            </div>
                            <div className="text-right">
                                <div className="font-display text-5xl text-[var(--hot)] leading-none">
                                    {active.score}%
                                </div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70 mt-2">
                                    resonance
                                </div>
                            </div>
                        </div>
                        <p className="mt-6 leading-relaxed text-[var(--paper)]/85">
                            {active.detail}
                        </p>
                        <p className="mt-5 font-hand text-2xl leading-tight text-[var(--hot)]">
                            {active.clue}
                        </p>
                    </div>
                    <div className="mt-6 grid grid-cols-2 gap-2 max-w-md">
                        {dimensions.slice(0, 8).map((d, index) => (
                            <div
                                key={d.name}
                                onMouseEnter={() => setActiveIndex(index)}
                                className="font-mono text-xs uppercase tracking-[0.15em] border border-[var(--paper)]/30 px-3 py-2.5 hover:border-[var(--hot)] hover:text-[var(--hot)] transition"
                            >
                                {d.name}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
